/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : NAVER MAP API를 실행하는 MAP 컴포넌트
 * 버전 : 1.0
 * 파일명 : SchoolMap.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정일 : 2025-07-02, 강동형 : 저작권 추가, 진입 시 즉시 검색하는 로직 개선, 더보기 리다이렉트 버튼에 따른 로직 추가
 * ================================================================ */


import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import e2 from "src/assets/images/marker_Element_2.svg"
import e4 from "src/assets/images/marker_Element_4.svg"
import normal from "src/assets/images/marker_Element_normal.svg"
import specialize from "src/assets/images/marker_Element_specialize.svg"
import purpose from "src/assets/images/marker_Element_purpose.svg"
import self from "src/assets/images/marker_Element_self.svg"
import Etc from "src/assets/images/marker_Element_Etc.svg"
import { getDateDifference } from "src/utils/DateUtils";
import { Coordinate } from "src/types/SchoolType";
import { useSchoolListDialogStore } from "src/stores/DialogStore";
import { LoadMapDataCurrentGeo } from "./LoadMapData";
import { useStorageStore } from "src/stores/StorageStore";
import { useModalStore } from "src/stores/ModalStore";
import { ModalContent_SchoolSearch, ModalFooter_SchoolSearch, ModalTitle_SchoolSearch } from "./ModalSchoolSearch";
import { ModalContent_SchoolSearchNoResult, ModalFooter_SchoolSearchNoResult, ModalTitle_SchoolSearchNoResult } from "./ModalSchoolSearch_NoResult";

const MapContainer = styled.div`
  width: 100%;
  height: calc(100vh - 251px);
`;

const loadNaverMapScript = (callback:()=>void) => {
  if (window.naver?.maps) {
    callback();
    return;
  }

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.REACT_APP_DEV_NAVER_CLIENT_ID}`;
  script.async = true;
  document.head.appendChild(script);
  script.onload = callback;
};
// school_id
function mapToCoordinate(school:any) : Coordinate{
  if (school.school_class === '고등학교') {
    return{
      schools: school,
      name : school.school_name,
      latitude : school.location_latitude,
      longtitude : school.location_longitude,
      visited_At : school.visited_at,
      school_Level : '고등학교',
      highSchool_Type : school.school_type,
      associate : school.military_agreement,
      major : school.preferred_major,
      major_detail : school.special_major_list,
      excluded: school.excluded
    }
  }
  else{
    return{
      schools: school,
      name : school.school_name,
      latitude : school.location_latitude,
      longtitude : school.location_longitude,
      visited_At : school.visited_at,
      school_Level : school.school_type,
      associate : school.military_agreement,
      major : school.preferred_major,
      major_detail : school.special_major_list,
      rotc :school.rotc,
      excluded: school.excluded
    }
  }
}

interface SchoolMapProps {
  schools?: any; // 여러 개의 주소를 받음
  children? : React.ReactNode;
  geo? : Geolocation
  trigger? : boolean;
  geoTrigger? :boolean
  setCenterPos : React.Dispatch<React.SetStateAction<boolean>>
}

const SchoolMap = ({ schools, geo, children, trigger, geoTrigger,setCenterPos }: SchoolMapProps) => {
  const mapRef = useRef<naver.maps.Map | null>(null); //지도객체
  const mapElement = useRef<HTMLDivElement | null>(null); //지도를 담을 div
  const markerRef = useRef<naver.maps.Marker[]>([]); //마커
  const usermarkerRef = useRef<naver.maps.Marker[]>([]); //마커
  const isPreset = useRef(true);
  const addresses = useMemo(() => schools.map(mapToCoordinate), [schools]);
  const setField = useSchoolListDialogStore((state)=>state.setField)
  const criterion = useSchoolListDialogStore((state)=>state.criterion)
  const [MoveCenter, setMoveCenter] = useState(true);
  const getUserdata = useStorageStore().getItem('user').data.user_area
  const modalSetField = useModalStore((state)=>state.setField)

  const getVisibleBoundsGeo = async () => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds() as naver.maps.LatLngBounds;
      const sw = bounds.getSW(); // SouthWest: 왼쪽 아래
      const ne = bounds.getNE(); // NorthEast: 오른쪽 위

      const minLat = sw.lat();
      const maxLat = ne.lat();
      const minLng = sw.lng();
      const maxLng = ne.lng();

      LoadMapDataCurrentGeo({min_lon:minLng,min_lat:minLat,max_lon:maxLng,max_lat:maxLat})
      setMoveCenter(false);
      // setSchools(response.data, 1);
    }
  };
  useEffect(()=>{
    getVisibleBoundsGeo()
  },[geoTrigger])

  useEffect(()=>{
    loadNaverMapScript(()=>{
      if(mapElement.current && isPreset.current){
        //지도 생성
        mapRef.current = new naver.maps.Map(mapElement.current,
          {
            center: new naver.maps.LatLng(37.5666, 126.9782),
            zoom: 10,
          });
        //중심 좌표 변경 리스너
        naver.maps.Event.addListener(mapRef.current!, 'center_changed', () => {
          mapRef.current?.getCenter() as naver.maps.LatLng;
          setCenterPos(false)
        });

        naver.maps.Event.addListener(mapRef.current, 'init', () => {
          modalSetField('type','CENTER')
          if(Object.keys(getUserdata).length === 0){
            modalSetField('title',<ModalTitle_SchoolSearch/>)
            modalSetField('content',<ModalContent_SchoolSearch/>)
            modalSetField('footer',<ModalFooter_SchoolSearch/>)
            modalSetField('open',true)
          }
          else{
            modalSetField('title',<ModalTitle_SchoolSearchNoResult/>)
            modalSetField('content',<ModalContent_SchoolSearchNoResult/>)
            modalSetField('footer',<ModalFooter_SchoolSearchNoResult/>)
            if(!criterion){
              // LoadMapDataFilter()
            }
          }
      });
      } else{
        // console.log('지도 이미 생성됨');
      }
    });
  },[]);

  useEffect(()=>{
    if (mapRef.current) {
      markerRef.current.forEach(marker => marker.setMap(null));
        if (addresses.length > 0) {
          // 중심 계산 (평균 좌표)
          const total = addresses.reduce(
            (acc:any, cur:any) => ({
              latSum: acc.latSum + cur.latitude,
              lngSum: acc.lngSum + cur.longtitude,
            }),
            { latSum: 0, lngSum: 0 }
          );

          const avgLat = total.latSum / addresses.length;
          const avgLng = total.lngSum / addresses.length;
          const center = new naver.maps.LatLng(avgLat, avgLng);

          // bounds 초기값 설정 (첫 주소 기준)
          const firstPos = new naver.maps.LatLng(addresses[0].latitude ?? 37.5666, addresses[0].longtitude ?? 126.9782);
          const bounds = new naver.maps.LatLngBounds(firstPos, firstPos);

          // 모든 마커 좌표 bounds에 포함시키기
          addresses.forEach(({ latitude, longtitude }:any) => {
            bounds.extend(new naver.maps.LatLng(latitude, longtitude));
          });
          if(MoveCenter){
          // 중심 이동 + 자동 줌
            if (addresses.length === 1) {
              mapRef.current.setCenter(center);
              mapRef.current.setZoom(14); // 적절한 줌
            } else {
              mapRef.current.setCenter(center);
              mapRef.current.fitBounds(bounds);
            }
          }
          setMoveCenter(true)
          PushMarker(addresses);
        }
      }
  },[addresses])

  useEffect(()=>{
    if(mapRef.current && geo){       
      usermarkerRef.current.forEach(marker => marker.setMap(null)); 
      myPosition(geo)
    }
  },[trigger])

  const createCustomMarker = (
    color: string,
    borderColor: string,
    iconUrl?: string,
    isColl?: boolean,
    showBadge?: boolean,
    schoolName?: string
  ):HTMLElement => {
    const size = 39; // SVG 원본 크기 기반
  
    // 마커 컨테이너 생성 (SVG + 텍스트 포함)
    const markerElement = document.createElement("div");
    markerElement.style.width = `${size}px`;
    markerElement.style.height = `47px`;
    markerElement.style.position = "relative";
    markerElement.style.display = "flex";
    markerElement.style.flexDirection = "column"; // 세로 정렬
    markerElement.style.alignItems = "center";   // 가운데 정렬
    markerElement.style.justifyContent = "center";
    markerElement.style.zIndex = "10";
    markerElement.style.transform = 'scale(1)'
    // SVG 마커 구조
    const svgMarker = `
      <svg xmlns="http://www.w3.org/2000/svg" width="39" height="47" viewBox="0 0 39 47" fill="none">
        <g filter="url(#filter0_d)">
         <path d="M18.5 42.2422C18.1644 42.2422 17.8315 42.1118 17.5738 41.8538L6.47315 30.6218C-0.157718 23.9125 -0.157718 12.9929 6.47315 6.28366C9.68658 3.03224 13.9577 1.24219 18.5 1.24219C23.0423 1.24219 27.3134 3.03224 30.5268 6.28366C37.1577 12.9929 37.1577 23.9098 30.5268 30.6218L19.4262 41.8538C19.1711 42.1118 18.8356 42.2422 18.5 42.2422Z" 
            fill="${color}" 
            stroke="${borderColor ? borderColor : '#fff'}" 
            stroke-width="${borderColor !== '#fff' ? 2 : 1}" 
            stroke-miterlimit="10"
          />
        </g>
        <defs>
          <filter id="filter0_d" x="0" y="0.742188" width="39" height="46" filterUnits="userSpaceOnUse">
            <feOffset dx="1" dy="2"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
          </filter>
        </defs>
      </svg>
    `;
  
    markerElement.innerHTML = svgMarker;
  
    // 🔹 내부 아이콘 추가
    if (iconUrl) {
      const img = document.createElement("img");
      img.src = iconUrl;
      img.style.width = isColl ? "40%" : "50%"; // 마커 크기의 50%
      img.style.height = isColl ? "40%" : "50%";
      img.style.position = "absolute";
      img.style.top = "40%"; // SVG의 중앙에 배치
      img.style.left = "47%"; // 정중앙 정렬
      img.style.transform = "translate(-50%, -50%)";
      img.style.objectFit = "contain";
      img.style.zIndex = "2"; // SVG 위에 표시
  
      markerElement.appendChild(img);
    }
  
    // 🔹 특정 값이 true일 경우 우상단에 작은 원 추가
    if (showBadge) {
      const badge = document.createElement("div");
      badge.style.position = "absolute";
      badge.style.top = "2px";
      badge.style.right = "2px";
      badge.style.width = "10px";
      badge.style.height = "10px";
      badge.style.backgroundColor = "#06799B";
      badge.style.borderRadius = "50%";
      badge.style.boxShadow = "0 0 5px rgba(0,0,0,0.3)";
      badge.style.border = "1px solid #fff";
      markerElement.appendChild(badge);
    }
  
    if (schoolName) {
      const nameElement = document.createElement("div");
      nameElement.textContent = schoolName;
      nameElement.style.position = "absolute";
      nameElement.style.top = "100%"; // 🔹 SVG 바로 아래 배치
      nameElement.style.left = "50%"; // 🔹 정중앙 정렬
      nameElement.style.transform = "translateX(-50%)"; // 🔹 정확한 가운데 정렬
      nameElement.style.width = "max-content"; // 🔹 텍스트 길이에 맞게 조정
      nameElement.style.textAlign = "center";
      nameElement.style.fontSize = "14px";
      nameElement.style.color = "black"; // 🔹 기본 텍스트 색상
      nameElement.style.fontWeight = "900"; // 🔹 글자 굵게
      nameElement.style.whiteSpace = "nowrap"; // 🔹 줄바꿈 방지
      nameElement.style.zIndex='2'
      // nameElement.style.webkitTextStroke = "0.3px white"; 

      const nameElementBack = document.createElement("div");
      nameElementBack.textContent = schoolName;
      nameElementBack.style.position = "absolute";
      nameElementBack.style.top = "100%"; // 🔹 SVG 바로 아래 배치
      nameElementBack.style.left = "50%"; // 🔹 정중앙 정렬
      nameElementBack.style.transform = "translateX(-50%)"; // 🔹 정확한 가운데 정렬
      nameElementBack.style.width = "max-content"; // 🔹 텍스트 길이에 맞게 조정
      nameElementBack.style.textAlign = "center";
      nameElementBack.style.fontSize = "14px";
      nameElementBack.style.color = "black"; // 🔹 기본 텍스트 색상
      nameElementBack.style.fontWeight = "900"; // 🔹 글자 굵게
      nameElementBack.style.whiteSpace = "nowrap"; // 🔹 줄바꿈 방지
      nameElementBack.style.zIndex='1'
      nameElementBack.style.webkitTextStroke = "4px white"; 


  
      markerElement.appendChild(nameElement);
      markerElement.appendChild(nameElementBack);
  }
  
  
    return markerElement;
  };

  // const [selectedMarker, setSelectedMarker] = useState<naver.maps.Marker | null>(null);
  const selectedMarkerRef = useRef<naver.maps.Marker | null>(null);
  const PushMarker = useCallback((e:Coordinate[] = [])=>{
    markerRef.current = [];

    //마커 생성
    e?.forEach(({name, latitude, longtitude, visited_At,school_Level,highSchool_Type,associate, rotc, excluded, schools})=>{
      if (excluded) return;
      if (!mapRef.current) return;
      const datediff = getDateDifference(visited_At)

      let color = "#F57749"
      if(!visited_At) { color = "#F57749" }
      else if (datediff <= 180) { color = "#57C0AA";} 
      else if (datediff <= 365) { color = "#5CA8E4";} 
      else if (datediff <= 730) { color = "#F5BE49";}
      else if (datediff >= 730) { color = "#F57749";}
      
      let iconic = ""
      let isColl = false
      if (school_Level === '대학교'){
        iconic = e4
        isColl = true
      }
      else if (school_Level?.includes('대학')){
        iconic = e2
        isColl = true
      }
      else if (school_Level === '훈련소' || school_Level === null){
        iconic = Etc
      }
      if (highSchool_Type ==="일반고등학교"){
        iconic = normal
      }
      if (highSchool_Type ==="특성화고등학교"){
        iconic = specialize
      }
      if (highSchool_Type ==="특수목적고등학교"){
        iconic = purpose
      }
      if (highSchool_Type ==="자율고등학교"){
        iconic = self
      }

      let borderColor = associate ? "#06799B":"#fff"

      let witharmy = rotc ? true: false
      
      const customMarker = createCustomMarker(color, borderColor ,iconic, isColl, witharmy, name)

      const marker = new naver.maps.Marker({
        position : new naver.maps.LatLng(latitude,longtitude),
        map : mapRef.current,
        icon: {
          content: customMarker, // HTML 기반 커스텀 마커
          anchor: new naver.maps.Point(19.5, 47)
        },
        zIndex : latitude*10
      });

      // 클릭 시 마커 커지게, 다른 마커는 원래대로
      naver.maps.Event.addListener(marker, 'click', () => {
        // 동일한 마커 다시 클릭
        if (selectedMarkerRef.current === marker) {
          setField('isOpen',true)
          setField('selected',schools)
          return;
        }
        // 이전 마커 축소
        if (selectedMarkerRef.current && selectedMarkerRef.current !== marker) {
          const prevIcon = selectedMarkerRef.current.getIcon() as naver.maps.MarkerOptions['icon'];
          if (prevIcon && typeof prevIcon === 'object' && 'content' in prevIcon) {
            const prevEl = prevIcon.content as HTMLElement;
            if (prevEl instanceof HTMLElement) {
              prevEl.style.transform = 'scale(1)';
              prevEl.style.zIndex = '10';
            }
          }
        }

        // 현재 마커 확대
      const icon = marker.getIcon() as naver.maps.MarkerOptions['icon'];
      if (icon && typeof icon === 'object' && 'content' in icon) {
        const el = icon.content as HTMLElement;
        if (el instanceof HTMLElement) {
          el.style.transform = 'scale(1.5) translateY(-8px)';
          el.style.zIndex = '20';
        }
      }
        selectedMarkerRef.current = marker;
      });
      markerRef.current.push(marker);
    })
  },[])

  const myPosition = (geo:Geolocation)=>{
    const map = mapRef.current
    if (!map) return

    usermarkerRef.current = [];

    const customDiv = document.createElement('div');
      customDiv.style.width = '18px';
      customDiv.style.height = '18px';
      customDiv.style.backgroundColor = '#06799B';
      customDiv.style.borderRadius = '100%';
      customDiv.style.boxShadow = '0 0 0 3px white';
      customDiv.style.filter = 'drop-shadow(0px 0px 10px rgba(6, 121, 155, 0.8))'; 


    geo.getCurrentPosition((position)=>{
      const {latitude,longitude} = position.coords

      const marker = new naver.maps.Marker({
        position: new naver.maps.LatLng(latitude,longitude),
        map : map,
        icon: {
          content : customDiv,
        }
      })
    usermarkerRef.current.push(marker)
    const currentPos = new naver.maps.LatLng(latitude, longitude);
    // 지도 중심과 현재 위치가 다르면 center 이동
    const center = map.getCenter() as naver.maps.LatLng;;
    if (
      center.lat() !== currentPos.lat() ||
      center.lng() !== currentPos.lng()
    ) {
      map.setCenter(currentPos);
    }
  })
  }

  
  return <MapContainer as="div" ref={mapElement}>
    {children}
  </MapContainer>;
};

export default SchoolMap;
